"use client"

import * as React from "react"
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays, subWeeks, subMonths, subYears } from "date-fns"
import { CalendarIcon, Clock } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"

export function DatePickerWithRange({ className, buttonClassName, dateRange, onDateRangeChange }) {
  const [date, setDate] = React.useState(
    dateRange?.from != null || dateRange?.to != null ? dateRange : undefined
  )
  const [previousDate, setPreviousDate] = React.useState(null)
  const [showCustomCalendar, setShowCustomCalendar] = React.useState(false)

  // Common date range presets
  const getPresetRanges = () => {
    const today = new Date()
    const yesterday = subDays(today, 1)
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    const thisWeekStart = startOfWeek(today, { weekStartsOn: 0 }) // Sunday
    const thisWeekEnd = endOfWeek(today, { weekStartsOn: 0 })
    const lastWeekStart = startOfWeek(subWeeks(today, 1), { weekStartsOn: 0 })
    const lastWeekEnd = endOfWeek(subWeeks(today, 1), { weekStartsOn: 0 })
    const thisMonthStart = startOfMonth(today)
    const thisMonthEnd = endOfMonth(today)
    const lastMonthStart = startOfMonth(subMonths(today, 1))
    const lastMonthEnd = endOfMonth(subMonths(today, 1))
    const thisYearStart = startOfYear(today)
    const thisYearEnd = endOfYear(today)
    const lastYearStart = startOfYear(subYears(today, 1))
    const lastYearEnd = endOfYear(subYears(today, 1))

    return [
      {
        label: "Today",
        value: {
          from: startOfDay(today),
          to: endOfDay(today)
        }
      },
      {
        label: "Tomorrow",
        value: {
          from: startOfDay(tomorrow),
          to: endOfDay(tomorrow)
        }
      },
      {
        label: "Yesterday",
        value: {
          from: startOfDay(yesterday),
          to: endOfDay(yesterday)
        }
      },
      {
        label: "This Week",
        value: {
          from: thisWeekStart,
          to: thisWeekEnd
        }
      },
      {
        label: "Last Week",
        value: {
          from: lastWeekStart,
          to: lastWeekEnd
        }
      },
      {
        label: "This Month",
        value: {
          from: thisMonthStart,
          to: thisMonthEnd
        }
      },
      {
        label: "Last Month",
        value: {
          from: lastMonthStart,
          to: lastMonthEnd
        }
      },
      {
        label: "Year to Date",
        value: {
          from: thisYearStart,
          to: endOfDay(today)
        }
      },
      {
        label: "Last Year",
        value: {
          from: lastYearStart,
          to: lastYearEnd
        }
      },
      {
        label: "Last 7 Days",
        value: {
          from: startOfDay(subDays(today, 6)),
          to: endOfDay(today)
        }
      },
      {
        label: "Last 30 Days",
        value: {
          from: startOfDay(subDays(today, 29)),
          to: endOfDay(today)
        }
      },
      {
        label: "Last 90 Days",
        value: {
          from: startOfDay(subDays(today, 89)),
          to: endOfDay(today)
        }
      }
    ]
  }

  const handlePresetSelect = (preset) => {
    // Store the preset label in the date object
    const dateWithLabel = {
      ...preset.value,
      label: preset.label
    };
    setDate(dateWithLabel)
    setShowCustomCalendar(false)
    if (onDateRangeChange) {
      onDateRangeChange(dateWithLabel)
    }
  }

  const handleCustomDateChange = (newDate) => {
    // Clear any existing label when custom dates are selected
    const customDate = {
      ...newDate,
      label: null
    };
    
    setDate(customDate);
  }

  // Update local state when dateRange prop changes
  React.useEffect(() => {
    if (dateRange?.from != null || dateRange?.to != null) {
      // Preserve the existing label if the dates are the same
      setDate(prevDate => {
        if (prevDate?.label && 
            prevDate?.from && prevDate?.to && 
            dateRange?.from && dateRange?.to) {
          // Check if the dates are the same
          const prevFrom = prevDate.from instanceof Date ? prevDate.from : new Date(prevDate.from);
          const prevTo = prevDate.to instanceof Date ? prevDate.to : new Date(prevDate.to);
          const newFrom = dateRange.from instanceof Date ? dateRange.from : new Date(dateRange.from);
          const newTo = dateRange.to instanceof Date ? dateRange.to : new Date(dateRange.to);
          
          if (prevFrom.getTime() === newFrom.getTime() && prevTo.getTime() === newTo.getTime()) {
            // Dates are the same, preserve the label
            return {
              ...dateRange,
              label: prevDate.label
            };
          }
        }
        // Dates are different or no previous label, use the new dateRange
        return dateRange;
      });
    } else {
      setDate(undefined);
    }
  }, [dateRange]);

  // Update local state and call the callback when date changes
  const handleDateChange = (newDate) => {
    // Helper function to convert to Date object
    const toDate = (dateValue) => {
      if (dateValue instanceof Date) return dateValue;
      if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
      }
      return null;
    };
    
    // If we have a current range and the new selection has a different start date
    if (date?.from && date?.to && newDate?.from && newDate?.to) {
      const currentFrom = toDate(date.from);
      const currentTo = toDate(date.to);
      const newFrom = toDate(newDate.from);
      const newTo = toDate(newDate.to);
      
      if (currentFrom && currentTo && newFrom && newTo) {
        // Check if the new start date is different from the current start date
        if (newFrom.getTime() !== currentFrom.getTime()) {
          // Set the new start date and clear the end date
          const updatedDate = {
            from: newFrom,
            to: undefined
          };
          setPreviousDate(date);
          setDate(updatedDate);
          if (onDateRangeChange) {
            onDateRangeChange(updatedDate);
          }
          return;
        }
        
        // Check if the clicked date is outside the current range
        const clickedDate = newTo;
        if (clickedDate < currentFrom || clickedDate > currentTo) {
          // Set the clicked date as the new start date and clear the end date
          const updatedDate = {
            from: clickedDate,
            to: undefined
          };
          setPreviousDate(date);
          setDate(updatedDate);
          if (onDateRangeChange) {
            onDateRangeChange(updatedDate);
          }
          return;
        }
      }
    }
    
    // Handle same date selection - set end date to end of day
    if (newDate?.from && newDate?.to) {
      const fromDate = toDate(newDate.from);
      const toDateValue = toDate(newDate.to);
      
      if (fromDate && toDateValue && fromDate.getTime() === toDateValue.getTime()) {
        const endOfDay = new Date(fromDate);
        endOfDay.setHours(23, 59, 59, 999);
        const updatedDate = {
          from: fromDate,
          to: endOfDay
        };
        setPreviousDate(date);
        setDate(updatedDate);
        if (onDateRangeChange) {
          onDateRangeChange(updatedDate);
        }
        return;
      }
    }
    
    // Normal behavior for other cases
    // Ensure we're passing valid Date objects
    const safeNewDate = newDate ? {
      from: newDate.from ? toDate(newDate.from) : undefined,
      to: newDate.to ? toDate(newDate.to) : undefined
    } : { from: undefined, to: undefined };
    
    setPreviousDate(date);
    setDate(safeNewDate);
    if (onDateRangeChange) {
      onDateRangeChange(safeNewDate);
    }
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={`w-full justify-start text-left font-normal ${!date && "text-muted-foreground"} ${buttonClassName}`}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            {date?.label ? (
              <span>{date.label}</span>
            ) : date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto min-w-80 lg:w-full lg:min-w-0 p-0" 
          align="start"
          onWheel={(e) => {
            e.preventDefault();
            const scrollContainer = e.currentTarget.querySelector('.scrollable-content');
            if (scrollContainer) {
              scrollContainer.scrollTop += e.deltaY;
            }
          }}
        >
          {!showCustomCalendar ? (
            /* Quick Select Options */
            <div 
              className="p-4 max-h-64 lg:max-h-none overflow-y-auto scrollable-content"
              style={{ 
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain'
              }}
              onTouchStart={(e) => {
                // Allow touch events to bubble up
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // Allow touch scrolling
                e.stopPropagation();
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Quick Select</span>
              </div>
              
              {/* Common Presets Grid */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {getPresetRanges().slice(0, 8).map((preset) => (
                  <Button
                    key={preset.label}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-left h-8 px-3 text-xs hover:bg-accent"
                    onClick={() => handlePresetSelect(preset)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              
              {/* Additional Presets */}
              <div className="space-y-1 mb-4">
                {getPresetRanges().slice(8).map((preset) => (
                  <Button
                    key={preset.label}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-left h-8 px-3 text-xs hover:bg-accent"
                    onClick={() => handlePresetSelect(preset)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              
              <Separator className="my-4" />
              
              {/* Custom Range Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 text-xs"
                onClick={() => setShowCustomCalendar(true)}
              >
                <CalendarIcon className="mr-2 h-3 w-3" />
                Custom Date Range
              </Button>
            </div>
          ) : (
            /* Custom Calendar */
            <div 
              className="p-4 max-h-80 sm:max-h-none overflow-y-auto scrollable-content"
              style={{ 
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain'
              }}
              onTouchStart={(e) => {
                // Allow touch events to bubble up
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // Allow touch scrolling
                e.stopPropagation();
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Custom Range</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => {
                    // Just return to preset options without applying changes
                    setShowCustomCalendar(false);
                  }}
                >
                  ←
                </Button>
              </div>
              
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={handleCustomDateChange}
                numberOfMonths={1}
                className="rounded-md border-0 w-full"
              />
              
              {/* Action Buttons */}
              <div className="flex gap-2 mt-4 pt-4 border-t sticky bottom-0 bg-background">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    // Set to today's date range
                    const today = new Date();
                    const todayRange = {
                      from: startOfDay(today),
                      to: endOfDay(today),
                      label: "Today"
                    };
                    setDate(todayRange);
                    // Custom calendar stays open for user to modify if needed
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  className="flex-1 dark:text-white"
                  onClick={() => {
                    // Apply the selected dates
                    if (onDateRangeChange && date?.from && date?.to) {
                      onDateRangeChange(date);
                    }
                    setShowCustomCalendar(false);
                  }}
                  disabled={!date?.from || !date?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
